<template>
  <main
    :class="[
      'single-film-prototype',
      'single-person-prototype',
      'single-film-list-prototype',
      'single-person-list-prototype',
      'discover-prototype',
      currentTab === 'shows' ? 'single-person-prototype--tv' : null,
    ]"
  >
    <section class="hero g-0 position-relative">
      <HeroTopPanel
        :logo-src="logoPath"
        :background-src="defaultBackgroundImage"
        :login-path="loginPath"
        @open-mobile-search="openMobileSearchOverlay"
      >
        <div class="container g-0 discover-hero-container">
          <div class="row g-0 discover-hero-row">
            <div class="col g-0">
              <div class="discover-hero-copy">
                <h1 data-testid="discover-page-title">Popular films, shows and people</h1>
                <p data-testid="discover-page-description">
                  From what’s trending today to fresh releases and timeless favourites.
                  <span
                    class="discover-intro__emphasis"
                    data-testid="discover-page-description-emphasis"
                  >
                    <br>
                    Show us what you’ve seen.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </HeroTopPanel>
    </section>

    <div class="container g-0 mobile-nav">
      <div class="row">
        <div class="col">
          <nav class="nav">
            <ul class="film-nav">
              <li>
                <RouterLink
                  :class="{ active: currentTab === 'films' }"
                  :to="filmsTabPath"
                  data-testid="discover-films-tab"
                >
                  Films
                </RouterLink>
              </li>
              <li>
                <RouterLink
                  :class="{ active: currentTab === 'shows' }"
                  :to="showsTabPath"
                  data-testid="discover-shows-tab"
                >
                  Shows
                </RouterLink>
              </li>
              <li>
                <RouterLink
                  :class="{ active: currentTab === 'people' }"
                  :to="peopleTabPath"
                  data-testid="discover-people-tab"
                >
                  People
                </RouterLink>
              </li>
            </ul>
          </nav>
          <nav
            v-if="currentTab === 'films'"
            class="discover-subnav"
            aria-label="Discover film sections"
            data-testid="discover-film-subtabs"
          >
            <ul class="discover-subnav__list">
              <li>
                <RouterLink
                  :class="{ active: currentFilmSubtab === 'popular-now' }"
                  :to="popularNowPath"
                  data-testid="discover-films-popular-now-tab"
                >
                  Popular now
                </RouterLink>
              </li>
              <li>
                <RouterLink
                  :class="{ active: currentFilmSubtab === 'all-time-greats' }"
                  :to="allTimeGreatsPath"
                  data-testid="discover-films-all-time-greats-tab"
                >
                  All-time greats
                </RouterLink>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <div class="container g-0">
      <div class="row g-0 film-overview">
        <div :class="overviewContentClass">
          <div class="container g-0">
            <div class="row g-0">
              <div class="col g-0">
                <div class="container g-0">
                  <div class="row g-0 sorting-buttons-row">
                    <div class="col g-0">
                      <div class="sorting-buttons">
                        <span class="sort-title">Sort:</span>
                        <template
                          v-for="button in sortButtons"
                          :key="button.key"
                        >
                          <a
                            :class="[
                              button.first ? 'first-btn' : null,
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            href="#"
                            @click.prevent="setSortMode(button.key)"
                          >
                            {{ button.label }}
                          </a>
                          <span
                            v-if="discoverSortRecommendedBreakVisible && button.key === 'position'"
                            class="discover-sort-recommended-break"
                            aria-hidden="true"
                          ></span>
                        </template>
                        <div
                          v-if="filtersButtonVisible"
                          class="modal"
                          data-testid="discover-filter-modal"
                          :style="{ display: filtersModalOpen ? 'flex' : 'none' }"
                          @click.self="toggleFilters"
                        >
                          <button
                            class="person-compact-rating-picker__close person-filter-modal__close"
                            data-testid="discover-filter-modal-close"
                            type="button"
                            aria-label="Close filters"
                            @click="toggleFilters"
                          >
                            <span class="person-compact-rating-picker__close-icon ion-close" aria-hidden="true"></span>
                          </button>
                          <div class="container person-filters">
                            <div
                              class="person-filter-modal__header"
                              data-testid="discover-filter-modal-header"
                            >
                              <h2 data-testid="discover-filter-modal-title">Filters:</h2>
                              <button
                                class="sorting-btn person-filter-modal__apply discover-filter-modal__apply-mobile"
                                data-testid="discover-filter-modal-apply"
                                type="button"
                                @click="toggleFilters"
                              >
                                Apply
                              </button>
                            </div>
                            <div
                              v-for="row in discoverFilterModalRows"
                              :key="row.key"
                              class="row g-0 filters-groups-row person-filter-modal__row"
                              data-testid="discover-filter-modal-row"
                            >
                              <div
                                v-for="group in row.groups"
                                :key="group.key"
                                :class="[
                                  'person-filter-modal__group',
                                  row.full ? 'person-filter-modal__group--full col-12' : 'person-filter-modal__group--half col-6',
                                ]"
                                data-testid="discover-filter-modal-group"
                              >
                                <div class="single-filter-title-row">
                                  <h3>{{ group.title }}</h3>
                                </div>
                                <div class="filter-row">
                                  <a
                                    v-for="option in group.options"
                                    :key="option.value"
                                    :class="{ active: option.active }"
                                    href="#"
                                    @click.prevent="applyFilter(group.key, option.value)"
                                  >
                                    {{ option.label }}
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="view-mode-btns">
                        <a
                          v-for="button in visibleViewModeButtons"
                          :key="`view-mode-${button.key}`"
                          :class="[
                            button.first ? 'first-btn' : null,
                            'view-mode-btn',
                            'sorting-btn',
                            button.active ? 'active' : null,
                          ]"
                          href="#"
                          :aria-label="button.label"
                          :title="button.label"
                          @click.prevent="setViewMode(button.key)"
                        >
                          <ViewModeIcon :mode="button.key" data-testid="discover-view-mode-icon" />
                        </a>
                        <button
                          v-if="filtersButtonVisible"
                          class="sorting-btn filter-btn view-mode-filter-btn"
                          data-testid="discover-mobile-filter-button"
                          type="button"
                          @click="toggleFilters"
                        >
                          {{ filterButtonLabel }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div ref="listContentStartRef" class="container g-0">
                    <div class="row g-0">
                      <div class="col g-0">
                        <p v-if="currentLoading" class="discover-empty-state">Loading...</p>
                        <p v-else-if="currentError" class="discover-empty-state">{{ currentError }}</p>

                        <template v-else-if="currentTab === 'films'">
                          <div
                            v-if="currentViewMode === '1'"
                            class="container g-0 credits-list film-list-detail__credits-list"
                          >
                            <template v-if="paginatedItems.length === 0">
                              <p class="discover-empty-state">
                                No films available.
                              </p>
                            </template>
                            <template v-else>
                              <template v-for="(entry, index) in paginatedItems" :key="entry.id">
                                <div class="row g-0 single-credit-row film-list-detail__row">
                                  <div class="col-3 g-0 film-poster-col film-list-detail__poster-col">
                                    <div class="film-poster-container">
                                      <a :href="entry.film.url || '#'"><img class="film-poster" :src="entry.film.poster_url || posterPlaceholderImage" :alt="`${entry.film.title} poster`"></a>
                                    </div>
                                  </div>
                                  <div class="col g-0">
                                    <div class="container g-0 single-credit-container film-list-detail__container">
                                      <div class="row g-0 credit-title-row">
                                        <div class="col g-0">
                                          <a :href="entry.film.url || '#'">
                                            <h3 class="credit-film-title">
                                              {{ entry.film.title }}
                                              <span v-if="entry.film.year" class="release-year">({{ entry.film.year }})</span>
                                            </h3>
                                          </a>
                                        </div>
                                      </div>
                                      <div v-if="entry.film.overview" class="row g-0 overview-row">
                                        <div class="col g-0">
                                          <p class="credit-film-overview discover-overview-text">
                                            <span :data-testid="`discover-film-overview-body-${filmOverviewEntryKey(entry)}`">{{ displayedFilmOverview(entry) }}</span>
                                            <button
                                              v-if="hasExpandableFilmOverview(entry)"
                                              class="overview-toggle"
                                              :data-testid="`discover-film-overview-toggle-${filmOverviewEntryKey(entry)}`"
                                              type="button"
                                              @click="toggleFilmOverview(entry)"
                                            >
                                              <span class="overview-toggle__icon" aria-hidden="true">{{ isFilmOverviewExpanded(entry) ? '▲' : '▼' }}</span>
                                            </button>
                                          </p>
                                        </div>
                                      </div>
                                      <div v-if="entry.film.directors.length" class="row g-0 directors-row first-detail-row-spaced">
                                        <div class="col g-0">
                                          <div class="directors">
                                            <p class="credit-film-runtime">Directed by:</p>
                                            <template v-for="(director, index) in entry.film.directors" :key="director.name">
                                              <a :href="director.url || '#'">{{ director.name }}</a>
                                              <span v-if="index < entry.film.directors.length - 1"> • </span>
                                            </template>
                                          </div>
                                        </div>
                                      </div>
                                      <div v-if="entry.film.stars.length" class="row g-0 stars-row">
                                        <div class="col g-0">
                                          <div class="stars">
                                            <p class="credit-film-runtime">Stars:</p>
                                            <template v-for="(star, index) in entry.film.stars" :key="star.name">
                                              <a :href="star.url || '#'">{{ star.name }}</a>
                                              <span v-if="index < entry.film.stars.length - 1"> • </span>
                                            </template>
                                          </div>
                                        </div>
                                      </div>
                                      <div class="row g-0 person-credits-button-row">
                                        <div class="col g-0">
                                          <div class="person-buttons person-buttons--credit">
                                            <template v-if="isAuthenticated">
                                              <button class="fs-btn left-btn" :class="{ 'btn--watched': entry.film.watched }" type="button" @click="markFilmWatched(entry)">
                                                <div class="default-btn"><i :class="entry.film.watched ? 'ion-checkmark' : 'ion-plus'"></i></div>
                                              </button>
                                              <button class="fs-btn middle-btn" :class="{ 'btn--watchlisted': entry.film.watchlisted }" type="button" @click="toggleFilmWatchlist(entry)">
                                                <div class="default-btn"><i class="ion-bookmark"></i></div>
                                              </button>
                                              <button class="fs-btn right-btn" :class="{ 'btn--favourited': entry.film.favourited }" type="button" @click="toggleFilmFavourite(entry)">
                                                <div class="default-btn"><i class="ion-heart"></i></div>
                                              </button>
                                            </template>
                                            <template v-else>
                                              <a class="fs-btn left-btn" :href="loginPath"><div class="default-btn"><i class="ion-plus"></i></div></a>
                                              <a class="fs-btn middle-btn" :href="loginPath"><div class="default-btn"><i class="ion-bookmark"></i></div></a>
                                              <a class="fs-btn right-btn" :href="loginPath"><div class="default-btn"><i class="ion-heart"></i></div></a>
                                            </template>
                                          </div>
                                        </div>
                                        <div class="col g-0">
                                          <div class="rating-stars person-compact-rating-stars person-compact-rating-stars--single">
                                            <template v-if="isAuthenticated">
                                              <button class="person-credit-rating-summary" type="button" @click="openRatingPicker('film', entry)">
                                                <i :class="summaryRatingStarClass(entry.film.rating)"></i>
                                              </button>
                                            </template>
                                            <template v-else>
                                              <a class="person-credit-rating-summary" :href="loginPath">
                                                <i class="rating-star ion-ios-star-outline"></i>
                                              </a>
                                            </template>
                                            <div class="current-credits-rating person-compact-current-rating">
                                              <p>{{ ratingText(entry.film.rating) }}</p>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div v-if="entry.film.overview" class="row g-0 overview-row-sm">
                                  <div class="col g-0">
                                    <p class="credit-film-overview discover-overview-text">
                                      <span :data-testid="`discover-film-overview-mobile-body-${filmOverviewEntryKey(entry)}`">{{ displayedFilmOverview(entry) }}</span>
                                      <button
                                        v-if="hasExpandableFilmOverview(entry)"
                                        class="overview-toggle"
                                        :data-testid="`discover-film-overview-mobile-toggle-${filmOverviewEntryKey(entry)}`"
                                        type="button"
                                        @click="toggleFilmOverview(entry)"
                                      >
                                        <span class="overview-toggle__icon" aria-hidden="true">{{ isFilmOverviewExpanded(entry) ? '▲' : '▼' }}</span>
                                      </button>
                                    </p>
                                  </div>
                                </div>
                                <div
                                  v-if="entry.film.directors.length"
                                  :class="[
                                    'row',
                                    'g-0',
                                    'directors-row-sm',
                                    { 'first-detail-row-spaced': shouldAddFilmDetailsTopGap(entry, 'directors') },
                                  ]"
                                >
                                  <div class="col g-0">
                                    <div class="directors">
                                      <p class="credit-film-runtime">Directed by:</p>
                                      <template v-for="(director, directorIndex) in entry.film.directors" :key="`${entry.film.title}-${director.name}`">
                                        <a :href="director.url || '#'">{{ director.name }}</a>
                                        <span v-if="directorIndex < entry.film.directors.length - 1"> • </span>
                                      </template>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  v-if="entry.film.stars.length"
                                  :class="[
                                    'row',
                                    'g-0',
                                    'stars-row-sm',
                                    { 'first-detail-row-spaced': shouldAddFilmDetailsTopGap(entry, 'stars') },
                                  ]"
                                >
                                  <div class="col g-0">
                                    <div class="stars">
                                      <p class="credit-film-runtime">Stars:</p>
                                      <template v-for="(star, starIndex) in entry.film.stars" :key="`${entry.film.title}-${star.name}`">
                                        <a :href="star.url || '#'">{{ star.name }}</a>
                                        <span v-if="starIndex < entry.film.stars.length - 1"> • </span>
                                      </template>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  :class="[
                                    'row',
                                    'g-0',
                                    'person-credits-button-row-sm',
                                    index === paginatedItems.length - 1 ? 'last-person-credits-button-row-sm' : null,
                                  ]"
                                  data-testid="discover-film-action-row-sm"
                                >
                                  <div class="col g-0">
                                    <div class="person-buttons person-buttons--credit" data-testid="discover-film-action-group-sm">
                                      <template v-if="isAuthenticated">
                                        <button class="fs-btn left-btn" :class="{ 'btn--watched': entry.film.watched }" type="button" @click="markFilmWatched(entry)">
                                          <div class="default-btn"><i :class="entry.film.watched ? 'ion-checkmark' : 'ion-plus'"></i></div>
                                        </button>
                                        <button class="fs-btn middle-btn" :class="{ 'btn--watchlisted': entry.film.watchlisted }" type="button" @click="toggleFilmWatchlist(entry)">
                                          <div class="default-btn"><i class="ion-bookmark"></i></div>
                                        </button>
                                        <button class="fs-btn right-btn" :class="{ 'btn--favourited': entry.film.favourited }" type="button" @click="toggleFilmFavourite(entry)">
                                          <div class="default-btn"><i class="ion-heart"></i></div>
                                        </button>
                                      </template>
                                      <template v-else>
                                        <a class="fs-btn left-btn" :href="loginPath"><div class="default-btn"><i class="ion-plus"></i></div></a>
                                        <a class="fs-btn middle-btn" :href="loginPath"><div class="default-btn"><i class="ion-bookmark"></i></div></a>
                                        <a class="fs-btn right-btn" :href="loginPath"><div class="default-btn"><i class="ion-heart"></i></div></a>
                                      </template>
                                    </div>
                                  </div>
                                  <div class="col g-0">
                                    <div class="rating-stars">
                                      <template v-if="isAuthenticated">
                                        <button class="person-credit-rating-summary" type="button" @click="openRatingPicker('film', entry)">
                                          <i :class="summaryRatingStarClass(entry.film.rating)"></i>
                                        </button>
                                      </template>
                                      <template v-else>
                                        <a class="person-credit-rating-summary" :href="loginPath">
                                          <i class="rating-star ion-ios-star-outline"></i>
                                        </a>
                                      </template>
                                      <div class="current-credits-rating">
                                        <p>{{ ratingText(entry.film.rating) }}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </template>
                            </template>
                          </div>

                          <div v-else-if="currentViewMode === '2'" class="person-posters-grid film-list-detail__posters-grid">
                            <p v-if="paginatedItems.length === 0" class="discover-empty-state">No films available.</p>
                            <div v-for="entry in paginatedItems" v-else :key="entry.id" class="film-list-detail__poster-card">
                              <div class="film-list-detail__poster-media">
                                <a class="person-poster-link" :href="entry.film.url || '#'">
                                  <img class="person-poster-grid-image" :src="entry.film.poster_url || posterPlaceholderImage" :alt="`${entry.film.title} poster`">
                                </a>
                              </div>
                            </div>
                          </div>

                          <div v-else-if="currentViewMode === '3'" class="container g-0 person-compact-credits-list">
                            <p v-if="paginatedItems.length === 0" class="discover-empty-state">No films available.</p>
                            <div v-for="entry in paginatedItems" v-else :key="entry.id" class="row g-0 person-compact-credit-row">
                              <div class="col g-0">
                                <div class="container g-0 person-compact-credit-container">
                                  <div class="row g-0 credit-title-row person-compact-credit-title-row">
                                    <div class="col g-0">
                                      <a :href="entry.film.url || '#'">
                                        <h3 class="credit-film-title person-compact-credit-film-title">
                                          {{ entry.film.title }}
                                          <span v-if="entry.film.year" class="release-year">({{ entry.film.year }})</span>
                                        </h3>
                                      </a>
                                    </div>
                                  </div>
                                  <div class="row g-0 person-compact-credit-actions-row">
                                    <div class="col g-0">
                                      <div class="person-buttons person-buttons--credit person-buttons--compact">
                                        <template v-if="isAuthenticated">
                                          <button class="fs-btn left-btn" :class="{ 'btn--watched': entry.film.watched }" type="button" @click="markFilmWatched(entry)"><div class="default-btn"><i :class="entry.film.watched ? 'ion-checkmark' : 'ion-plus'"></i></div></button>
                                          <button class="fs-btn middle-btn" :class="{ 'btn--watchlisted': entry.film.watchlisted }" type="button" @click="toggleFilmWatchlist(entry)"><div class="default-btn"><i class="ion-bookmark"></i></div></button>
                                          <button class="fs-btn right-btn" :class="{ 'btn--favourited': entry.film.favourited }" type="button" @click="toggleFilmFavourite(entry)"><div class="default-btn"><i class="ion-heart"></i></div></button>
                                        </template>
                                        <template v-else>
                                          <a class="fs-btn left-btn" :href="loginPath"><div class="default-btn"><i class="ion-plus"></i></div></a>
                                          <a class="fs-btn middle-btn" :href="loginPath"><div class="default-btn"><i class="ion-bookmark"></i></div></a>
                                          <a class="fs-btn right-btn" :href="loginPath"><div class="default-btn"><i class="ion-heart"></i></div></a>
                                        </template>
                                      </div>
                                    </div>
                                    <div class="col g-0">
                                      <div
                                        class="rating-stars person-compact-rating-stars person-compact-rating-stars--full"
                                        @mouseleave="resetCompactRatingPreview('film', entry)"
                                      >
                                        <div class="person-compact-rating-stars-list" data-testid="discover-compact-full-stars">
                                          <template v-if="isAuthenticated">
                                            <button
                                              v-for="starNumber in 10"
                                              :key="`discover-film-${entry.film.id}-compact-star-${starNumber}`"
                                              class="person-compact-rating-star-button"
                                              type="button"
                                              @mouseenter="previewCompactRating('film', entry, starNumber)"
                                              @click="setCompactRating('film', entry, starNumber)"
                                            >
                                              <i :class="compactRatingStarClass('film', entry, starNumber)"></i>
                                            </button>
                                          </template>
                                          <template v-else>
                                            <a
                                              v-for="starNumber in 10"
                                              :key="`discover-film-${entry.film.id}-compact-star-${starNumber}`"
                                              :href="loginPath"
                                            >
                                              <i class="rating-star ion-ios-star-outline"></i>
                                            </a>
                                          </template>
                                        </div>
                                        <div class="current-credits-rating person-compact-current-rating">
                                          <p>{{ ratingText(entry.film.rating) }}</p>
                                        </div>
                                      </div>
                                      <div class="rating-stars person-compact-rating-stars person-compact-rating-stars--single">
                                        <template v-if="isAuthenticated">
                                          <button class="person-credit-rating-summary" type="button" @click="openRatingPicker('film', entry)">
                                            <i :class="summaryRatingStarClass(entry.film.rating)"></i>
                                          </button>
                                        </template>
                                        <template v-else>
                                          <a class="person-credit-rating-summary" :href="loginPath">
                                            <i class="rating-star ion-ios-star-outline"></i>
                                          </a>
                                        </template>
                                        <div class="current-credits-rating person-compact-current-rating">
                                          <p>{{ ratingText(entry.film.rating) }}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div v-else class="container g-0 person-text-only-credits-list">
                            <p v-if="paginatedItems.length === 0" class="discover-empty-state">No films available.</p>
                            <div v-for="entry in paginatedItems" v-else :key="entry.id" class="row g-0 person-text-only-credit-row">
                              <div class="col g-0">
                                <div class="container g-0 person-text-only-credit-container">
                                  <div class="row g-0 credit-title-row">
                                    <div class="col g-0">
                                      <div class="person-text-only-row">
                                        <a :href="entry.film.url || '#'">
                                          <h3 class="credit-film-title person-text-only-credit-film-title">{{ entry.film.title }}</h3>
                                        </a>
                                        <span v-if="entry.film.year" class="release-year person-text-only-release-year">
                                          <span class="person-text-only-parenthesis">(</span>{{ entry.film.year }}<span class="person-text-only-parenthesis">)</span>
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>

                        <template v-else-if="currentTab === 'shows'">
                          <div
                            v-if="currentViewMode === '1'"
                            class="container g-0 credits-list person-tv-credits-list film-list-detail__credits-list"
                          >
                            <template v-if="paginatedItems.length === 0">
                              <p class="discover-empty-state">No shows available.</p>
                            </template>
                            <template v-else>
                              <div
                                v-for="entry in paginatedItems"
                                :key="entry.id"
                                class="person-tv-credit-group"
                                data-testid="discover-show-credit-group"
                              >
                                <div class="row g-0 single-credit-row person-tv-credit-row film-list-detail__row">
                                  <div class="col-3 g-0 film-poster-col film-list-detail__poster-col">
                                    <div class="film-poster-container">
                                      <a :href="entry.show.url || '#'"><img class="film-poster" :src="entry.show.poster_url || posterPlaceholderImage" :alt="`${entry.show.title} poster`"></a>
                                    </div>
                                  </div>
                                  <div class="col g-0">
                                    <div class="container g-0 single-credit-container film-list-detail__container">
                                      <div class="row g-0 credit-title-row">
                                        <div class="col g-0">
                                          <a :href="entry.show.url || '#'">
                                            <h3 class="credit-film-title">
                                              {{ entry.show.title }}
                                              <span v-if="entry.show.year" class="release-year">({{ entry.show.year }})</span>
                                            </h3>
                                          </a>
                                        </div>
                                      </div>
                                      <div v-if="entry.show.overview" class="row g-0 overview-row">
                                        <div class="col g-0">
                                          <p class="credit-film-overview">{{ entry.show.overview }}</p>
                                        </div>
                                      </div>
                                      <div class="row g-0">
                                        <div class="col g-0">
                                          <div class="container g-0 single-show-specs">
                                            <div v-if="entry.show.main_cast.length" class="row g-0 stars-row">
                                              <div class="col g-0">
                                                <div class="stars person-tv-main-cast">
                                                  <span class="credit-film-runtime">Main cast:</span>
                                                  <span
                                                    v-for="star in entry.show.main_cast"
                                                    :key="`${entry.show.title}-${star.name}`"
                                                    class="person-tv-cast-item"
                                                  >
                                                    <a :href="star.url || '#'">{{ star.name }}</a>
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div class="row g-0 person-show-credits-button-row">
                                        <div class="col g-0">
                                          <div class="person-show-buttons person-show-buttons--without-calendar">
                                            <template v-if="isAuthenticated">
                                              <button class="fs-btn left-btn" :class="{ 'btn--watchlisted': entry.show.watchlisted }" type="button" @click="toggleShowWatchlist(entry)">
                                                <div class="default-btn">
                                                  <i class="ion-bookmark"></i>&nbsp;<span class="watchlist-long">{{ showWatchlistButtonText(entry) }}</span>
                                                  <span class="watchlist-short">{{ showWatchlistButtonShortText(entry) }}</span>
                                                </div>
                                              </button>
                                              <button class="fs-btn right-btn" :class="{ 'btn--favourited': entry.show.favourited }" type="button" @click="toggleShowFavourite(entry)">
                                                <div class="default-btn">
                                                  <i class="ion-heart"></i>&nbsp;<span class="fav-long">{{ showFavouriteButtonText(entry) }}</span>
                                                  <span class="fav-short">{{ showFavouriteButtonShortText(entry) }}</span>
                                                </div>
                                              </button>
                                            </template>
                                            <template v-else>
                                              <a class="fs-btn left-btn" :href="loginPath">
                                                <div class="default-btn">
                                                  <i class="ion-bookmark"></i>&nbsp;<span class="watchlist-long">Add to watchlist</span>
                                                  <span class="watchlist-short">Watchlist</span>
                                                </div>
                                              </a>
                                              <a class="fs-btn right-btn" :href="loginPath">
                                                <div class="default-btn">
                                                  <i class="ion-heart"></i>&nbsp;<span class="fav-long">Add to favourites</span>
                                                  <span class="fav-short">Favourite</span>
                                                </div>
                                              </a>
                                            </template>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div class="row g-0 person-show-credits-button-row-sm" data-testid="discover-show-action-row-sm">
                                  <div class="col g-0">
                                    <div class="person-show-buttons person-show-buttons--without-calendar" data-testid="discover-show-action-group-sm">
                                      <template v-if="isAuthenticated">
                                        <button class="fs-btn left-btn" :class="{ 'btn--watchlisted': entry.show.watchlisted }" type="button" @click="toggleShowWatchlist(entry)">
                                          <div class="default-btn">
                                            <i class="ion-bookmark"></i>&nbsp;<span class="watchlist-long">{{ showWatchlistButtonText(entry) }}</span>
                                            <span class="watchlist-short">{{ showWatchlistButtonShortText(entry) }}</span>
                                          </div>
                                        </button>
                                        <button class="fs-btn right-btn" :class="{ 'btn--favourited': entry.show.favourited }" type="button" @click="toggleShowFavourite(entry)">
                                          <div class="default-btn">
                                            <i class="ion-heart"></i>&nbsp;<span class="fav-long">{{ showFavouriteButtonText(entry) }}</span>
                                            <span class="fav-short">{{ showFavouriteButtonShortText(entry) }}</span>
                                          </div>
                                        </button>
                                      </template>
                                      <template v-else>
                                        <a class="fs-btn left-btn" :href="loginPath">
                                          <div class="default-btn">
                                            <i class="ion-bookmark"></i>&nbsp;<span class="watchlist-long">Add to watchlist</span>
                                            <span class="watchlist-short">Watchlist</span>
                                          </div>
                                        </a>
                                        <a class="fs-btn right-btn" :href="loginPath">
                                          <div class="default-btn">
                                            <i class="ion-heart"></i>&nbsp;<span class="fav-long">Add to favourites</span>
                                            <span class="fav-short">Favourite</span>
                                          </div>
                                        </a>
                                      </template>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </template>
                          </div>

                          <div v-else-if="currentViewMode === '2'" class="person-posters-grid film-list-detail__posters-grid">
                            <p v-if="paginatedItems.length === 0" class="discover-empty-state">No shows available.</p>
                            <div v-for="entry in paginatedItems" v-else :key="entry.id" class="film-list-detail__poster-card">
                              <div class="film-list-detail__poster-media">
                                <a class="person-poster-link" :href="entry.show.url || '#'">
                                  <img class="person-poster-grid-image" :src="entry.show.poster_url || posterPlaceholderImage" :alt="`${entry.show.title} poster`">
                                </a>
                              </div>
                            </div>
                          </div>

                          <div v-else-if="currentViewMode === '3'" class="container g-0 person-compact-credits-list">
                            <p v-if="paginatedItems.length === 0" class="discover-empty-state">No shows available.</p>
                            <div v-for="entry in paginatedItems" v-else :key="entry.id" class="row g-0 person-compact-credit-row">
                              <div class="col g-0">
                                <div class="container g-0 person-compact-credit-container">
                                  <div class="row g-0 credit-title-row person-compact-credit-title-row">
                                    <div class="col g-0">
                                      <a :href="entry.show.url || '#'">
                                        <h3 class="credit-film-title person-compact-credit-film-title">
                                          {{ entry.show.title }}
                                          <span v-if="entry.show.year" class="release-year">({{ entry.show.year }})</span>
                                        </h3>
                                      </a>
                                    </div>
                                  </div>
                                  <div class="row g-0 person-compact-credit-actions-row">
                                    <div class="col g-0">
                                      <div class="person-buttons person-buttons--credit person-buttons--compact person-buttons--without-calendar">
                                        <template v-if="isAuthenticated">
                                          <button class="fs-btn left-btn" :class="{ 'btn--watchlisted': entry.show.watchlisted }" type="button" @click="toggleShowWatchlist(entry)"><div class="default-btn"><i class="ion-bookmark"></i></div></button>
                                          <button class="fs-btn right-btn" :class="{ 'btn--favourited': entry.show.favourited }" type="button" @click="toggleShowFavourite(entry)"><div class="default-btn"><i class="ion-heart"></i></div></button>
                                        </template>
                                        <template v-else>
                                          <a class="fs-btn left-btn" :href="loginPath"><div class="default-btn"><i class="ion-bookmark"></i></div></a>
                                          <a class="fs-btn right-btn" :href="loginPath"><div class="default-btn"><i class="ion-heart"></i></div></a>
                                        </template>
                                      </div>
                                    </div>
                                    <div class="col g-0">
                                      <div
                                        class="rating-stars person-compact-rating-stars person-compact-rating-stars--full"
                                        @mouseleave="resetCompactRatingPreview('show', entry)"
                                      >
                                        <div class="person-compact-rating-stars-list" data-testid="discover-compact-full-stars">
                                          <template v-if="isAuthenticated">
                                            <button
                                              v-for="starNumber in 10"
                                              :key="`discover-show-${entry.show.id}-compact-star-${starNumber}`"
                                              class="person-compact-rating-star-button"
                                              type="button"
                                              @mouseenter="previewCompactRating('show', entry, starNumber)"
                                              @click="setCompactRating('show', entry, starNumber)"
                                            >
                                              <i :class="compactRatingStarClass('show', entry, starNumber)"></i>
                                            </button>
                                          </template>
                                          <template v-else>
                                            <a
                                              v-for="starNumber in 10"
                                              :key="`discover-show-${entry.show.id}-compact-star-${starNumber}`"
                                              :href="loginPath"
                                            >
                                              <i class="rating-star ion-ios-star-outline"></i>
                                            </a>
                                          </template>
                                        </div>
                                        <div class="current-credits-rating person-compact-current-rating">
                                          <p>{{ ratingText(entry.show.rating) }}</p>
                                        </div>
                                      </div>
                                      <div class="rating-stars person-compact-rating-stars person-compact-rating-stars--single">
                                        <template v-if="isAuthenticated">
                                          <button class="person-credit-rating-summary" type="button" @click="openRatingPicker('show', entry)">
                                            <i :class="summaryRatingStarClass(entry.show.rating)"></i>
                                          </button>
                                        </template>
                                        <template v-else>
                                          <a class="person-credit-rating-summary" :href="loginPath">
                                            <i class="rating-star ion-ios-star-outline"></i>
                                          </a>
                                        </template>
                                        <div class="current-credits-rating person-compact-current-rating">
                                          <p>{{ ratingText(entry.show.rating) }}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div v-else class="container g-0 person-text-only-credits-list">
                            <p v-if="paginatedItems.length === 0" class="discover-empty-state">No shows available.</p>
                            <div v-for="entry in paginatedItems" v-else :key="entry.id" class="row g-0 person-text-only-credit-row">
                              <div class="col g-0">
                                <div class="container g-0 person-text-only-credit-container">
                                  <div class="row g-0 credit-title-row">
                                    <div class="col g-0">
                                      <div class="person-text-only-row">
                                        <a :href="entry.show.url || '#'">
                                          <h3 class="credit-film-title person-text-only-credit-film-title">{{ entry.show.title }}</h3>
                                        </a>
                                        <span v-if="entry.show.year" class="release-year person-text-only-release-year">
                                          <span class="person-text-only-parenthesis">(</span>{{ entry.show.year }}<span class="person-text-only-parenthesis">)</span>
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>

                        <template v-else>
                          <div v-if="currentViewMode === '1'" class="container g-0 credits-list film-list-detail__credits-list">
                            <p v-if="paginatedItems.length === 0" class="discover-empty-state discover-person-list-empty">No people available.</p>
                            <div v-for="entry in paginatedItems" v-else :key="entry.id" class="row g-0 single-credit-row film-list-detail__row discover-person-full-row">
                              <div class="col-3 g-0 film-poster-col film-list-detail__poster-col">
                                <div class="film-poster-container">
                                  <a :href="entry.person.url || '#'">
                                    <img class="film-poster person-list-detail__portrait-image" :src="entry.person.poster_url || portraitPlaceholderImage" :alt="`${entry.person.name} portrait`">
                                  </a>
                                </div>
                              </div>
                              <div class="col g-0">
                                <div class="container g-0 single-credit-container film-list-detail__container person-list-detail__container">
                                  <div class="row g-0 credit-title-row person-list-detail__title-row">
                                    <div class="col g-0">
                                      <a :href="entry.person.url || '#'">
                                        <h3 class="credit-film-title person-list-detail__name">{{ entry.person.name }}</h3>
                                      </a>
                                    </div>
                                  </div>
                                  <div class="row g-0 person-list-detail__button-row">
                                    <div class="col g-0">
                                      <template v-if="isAuthenticated">
                                        <button class="btn single-btn person-list-detail__favourite-button" :class="{ 'btn--favourited': entry.person.favourited }" type="button" @click="togglePersonFavourite(entry)">
                                          <div class="default-btn">
                                            <i class="ion-heart"></i>
                                            <span class="fav-btn">{{ entry.person.favourited ? 'Favourited' : 'Add to favourites' }}</span>
                                            <span class="fav-btn-sm">{{ entry.person.favourited ? 'Favourited' : 'Favourite' }}</span>
                                          </div>
                                        </button>
                                      </template>
                                      <template v-else>
                                        <a class="btn single-btn person-list-detail__favourite-button" :href="loginPath">
                                          <div class="default-btn">
                                            <i class="ion-heart"></i>
                                            <span class="fav-btn">Add to favourites</span>
                                            <span class="fav-btn-sm">Favourite</span>
                                          </div>
                                        </a>
                                      </template>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div v-else-if="currentViewMode === '2'" class="person-posters-grid film-list-detail__posters-grid">
                            <p v-if="paginatedItems.length === 0" class="discover-empty-state">No people available.</p>
                            <div v-for="entry in paginatedItems" v-else :key="entry.id" class="film-list-detail__poster-card">
                              <div class="film-list-detail__poster-media">
                                <a class="person-poster-link" :href="entry.person.url || '#'">
                                  <img class="person-poster-grid-image person-list-detail__poster-grid-image" :src="entry.person.poster_url || portraitPlaceholderImage" :alt="`${entry.person.name} portrait`">
                                </a>
                              </div>
                            </div>
                          </div>

                          <div v-else-if="currentViewMode === '3'" class="container g-0 person-compact-credits-list">
                            <p v-if="paginatedItems.length === 0" class="discover-empty-state">No people available.</p>
                            <div v-for="entry in paginatedItems" v-else :key="entry.id" class="row g-0 person-compact-credit-row">
                              <div class="col g-0">
                                <div class="container g-0 person-compact-credit-container">
                                  <div class="row g-0 credit-title-row person-compact-credit-title-row">
                                    <div class="col g-0 discover-person-compact-row">
                                      <a :href="entry.person.url || '#'">
                                        <h3 class="credit-film-title person-compact-credit-film-title">{{ entry.person.name }}</h3>
                                      </a>
                                      <div class="discover-person-compact-actions">
                                        <template v-if="isAuthenticated">
                                          <button class="btn-sm person-list-detail__favourite-button" :class="{ 'btn--favourited': entry.person.favourited }" type="button" @click="togglePersonFavourite(entry)">
                                            {{ entry.person.favourited ? 'Favourited' : 'Favourite' }}
                                          </button>
                                        </template>
                                        <template v-else>
                                          <a class="btn-sm person-list-detail__favourite-button" :href="loginPath">Favourite</a>
                                        </template>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div v-else class="container g-0 person-text-only-credits-list film-list-detail__text-list">
                            <p v-if="paginatedItems.length === 0" class="discover-empty-state">No people available.</p>
                            <div v-for="entry in paginatedItems" v-else :key="entry.id" class="row g-0 person-text-only-credit-row film-list-detail__text-row">
                              <div class="col g-0 person-text-only-credit-container">
                                <div class="person-text-only-row person-list-detail__text-row">
                                  <a :href="entry.person.url || '#'" class="film-list-detail__text-title-link">
                                    <h3 class="person-text-only-credit-film-title person-list-detail__text-name discover-person-text-name">
                                      {{ entry.person.name }}
                                    </h3>
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>

                        <nav
                          v-if="effectiveNumPages > 1"
                          class="discover-pagination discover-pagination--with-mobile mt-3"
                          aria-label="Discover pagination"
                        >
                          <ul class="pagination mb-0">
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                              <button type="button" class="page-link" @click="goToPage(currentPage - 1)">Previous</button>
                            </li>
                            <li
                              v-for="pageItem in pages"
                              :key="pageItem.key"
                              class="page-item"
                              :class="{ active: pageItem.page === currentPage, disabled: pageItem.ellipsis }"
                            >
                              <span v-if="pageItem.ellipsis" class="page-link" aria-hidden="true">
                                {{ pageItem.label }}
                              </span>
                              <button
                                v-else
                                type="button"
                                class="page-link"
                                :aria-label="`Go to page ${pageItem.page}`"
                                @click="goToPage(pageItem.page)"
                              >
                                {{ pageItem.label }}
                              </button>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === effectiveNumPages }">
                              <button type="button" class="page-link" @click="goToPage(currentPage + 1)">Next</button>
                            </li>
                          </ul>
                          <CompactMobilePagination
                            :current-page="currentPage"
                            :total-pages="effectiveNumPages"
                            test-id-prefix="discover"
                            @page-change="goToPage"
                          />
                        </nav>
                      </div>

                      <div v-if="filtersColumnVisible" class="col-3 g-0 filters-section">
                        <div class="container person-filters">
                          <div class="row g-0 filters-title-row">
                            <div class="col g-0">
                              <h3>Filters:</h3>
                            </div>
                          </div>

                          <template v-for="group in filterGroups" :key="`sidebar-${group.key}`">
                            <div class="row g-0 single-filter-title-row">
                              <div class="col g-0">
                                <h3>{{ group.title }}</h3>
                              </div>
                            </div>
                            <div class="row g-0 filter-row">
                              <div class="col g-0">
                                <a
                                  v-for="option in group.options"
                                  :key="option.value"
                                  :class="{ active: option.active }"
                                  href="#"
                                  @click.prevent="applyFilter(group.key, option.value)"
                                >
                                  {{ option.label }}
                                </a>
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="selectedRatingItem"
        class="person-compact-rating-picker-overlay"
        @click.self="closeRatingPicker"
      >
        <div class="person-compact-rating-picker">
          <button
            class="person-compact-rating-picker__close"
            type="button"
            aria-label="Close rating picker"
            @click="closeRatingPicker"
          >
            <span class="person-compact-rating-picker__close-icon ion-close" aria-hidden="true"></span>
          </button>

          <p class="person-compact-rating-picker__title">
            Rate {{ ratingPickerTitle }}
          </p>
          <div class="person-compact-rating-picker__stars">
            <button
              v-for="star in 10"
              :key="`discover-rating-${selectedRatingKind}-${star}`"
              class="person-compact-rating-picker__star"
              type="button"
              @mouseenter="previewRating(star)"
              @mouseleave="resetRatingPreview"
              @click="selectRating(star)"
            >
              <i :class="ratingPickerStarClass(star)"></i>
            </button>
          </div>
          <button
            class="person-compact-rating-picker__reset"
            type="button"
            :disabled="selectedRatingValue === null"
            @click="resetSelectedItemRating"
          >
            Reset rating
          </button>
        </div>
      </div>
    </Teleport>

    <MobileSearchOverlay :open="mobileSearchOpen" @close="closeMobileSearchOverlay" />
  </main>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import HeroTopPanel from '../components/HeroTopPanel.vue'
import MobileSearchOverlay from '../components/MobileSearchOverlay.vue'
import CompactMobilePagination from '../components/CompactMobilePagination.vue'
import ViewModeIcon from '../components/ViewModeIcon'
import '../assets/single-film/bootstrap-lite.css'
import '../assets/single-film/styles.css'
import '../assets/single-person/person-detail.css'
import '../assets/single-person/person-show.css'
import '../assets/single-film-list/styles.css'
import '../assets/single-person-list/styles.css'
import '../assets/discover/styles.css'
import { useAuthStore } from '../stores/auth'
import { buildLoginPath } from '../utils/auth'
import { buildDocumentTitle, setDocumentTitle } from '../utils/documentTitle'
import { clipOverviewWithSuffix, hasExpandableOverview } from '../utils/overview'
import { compactPaginationItems } from '../utils/pagination'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
auth.initFromStorage()

const logoPath = '/static/spa/branding/logo.png'
const defaultBackgroundImage = '/media/films/backgrounds/default-bg.jpg'
const posterPlaceholderImage = '/static/films/img/placeholder-film-camera.svg'
const portraitPlaceholderImage = '/static/films/img/placeholder-person.svg'
const pageSize = 50
const filmPageSize = 30
const showPageSize = 30
const personPageSize = 30
const discoverFiltersColumnMaxWidth = 900
const discoverViewModeStorageKey = 'filmsomniac-discover-view-mode'
const filmInteractionSource = 'F'
const showInteractionSource = 'S'
const personInteractionSource = 'P'

const mobileSearchOpen = ref(false)
const filtersModalOpen = ref(false)
const filmResults = ref([])
const showResults = ref([])
const personResults = ref([])
const filmCount = ref(0)
const filmNumPages = ref(1)
const filmFilterCounts = ref({
  release_status: { all: 0, released: 0, unreleased: 0 },
  activity: {
    all: 0,
    watched: 0,
    unwatched: 0,
    watchlisted: 0,
    rated: 0,
    favourited: 0,
  },
})
const showCount = ref(0)
const showNumPages = ref(1)
const showFilterCounts = ref({
  release_status: { all: 0, upcoming: 0, ongoing: 0, ended: 0 },
  activity: { all: 0, watchlisted: 0, rated: 0, favourited: 0 },
})
const personCount = ref(0)
const personNumPages = ref(1)
const personFilterCounts = ref({
  activity: { all: 0, favourited: 0 },
})
const filmLoading = ref(false)
const showLoading = ref(false)
const personLoading = ref(false)
const filmError = ref('')
const showError = ref('')
const personError = ref('')
const currentPage = ref(1)
const currentViewMode = ref(localStorage.getItem(discoverViewModeStorageKey) || '1')
const sortMode = ref('position')
const selectedReleaseStatus = ref('all')
const selectedActivity = ref('all')
const listContentStartRef = ref(null)
const selectedRatingKind = ref('')
const selectedRatingItem = ref(null)
const ratingPreview = ref(0)
const expandedFilmOverviewIds = ref({})
const compactRatingPreviewByKey = ref({})
const syncingFromRoute = ref(false)
const viewportWidth = ref(typeof window === 'undefined' ? 1200 : window.innerWidth)

const authUsername = computed(() => auth.state.username || 'Account')
const isAuthenticated = computed(() => auth.isAuthenticated.value)
const tabParam = computed(() => String(route.params.tab || '').toLowerCase())
const currentTab = computed(() => (
  ['shows', 'people'].includes(tabParam.value) ? tabParam.value : 'films'
))
const loginPath = computed(() => buildLoginPath(route.fullPath || '/discover'))
const filmsTabPath = computed(() => discoverFilmSectionPath('popular'))
const showsTabPath = computed(() => discoverPathForTab('shows'))
const peopleTabPath = computed(() => discoverPathForTab('people'))
const currentFilmSubtab = computed(() => (
  normalizedFilmSection(route.params.section) === 'all-time-greats'
    ? 'all-time-greats'
    : 'popular-now'
))
const popularNowPath = computed(() => discoverFilmSectionPath('popular'))
const allTimeGreatsPath = computed(() => discoverFilmSectionPath('all-time-greats'))
const discoverDocumentTitle = computed(() => {
  if (currentTab.value === 'shows') {
    return buildDocumentTitle(['Discover', 'Shows'])
  }
  if (currentTab.value === 'people') {
    return buildDocumentTitle(['Discover', 'People'])
  }
  return buildDocumentTitle(['Discover', 'Films'])
})

const overviewContentClass = computed(() => (
  filtersColumnVisible.value ? 'col-12 col-sm-12 col-md-12 col-lg-9' : 'col-12 col-sm-12 col-md-12 col-lg-10'
))

const currentResults = computed(() => {
  if (currentTab.value === 'shows') return showResults.value
  if (currentTab.value === 'people') return personResults.value
  return filmResults.value
})

const currentLoading = computed(() => {
  if (currentTab.value === 'shows') return showLoading.value
  if (currentTab.value === 'people') return personLoading.value
  return filmLoading.value
})

const currentError = computed(() => {
  if (currentTab.value === 'shows') return showError.value
  if (currentTab.value === 'people') return personError.value
  return filmError.value
})

const sortButtons = computed(() => {
  if (currentTab.value === 'people') {
    return [
      { key: 'position', label: 'Recommended', first: true, active: sortMode.value === 'position' },
      { key: 'name-asc', label: 'Name A-Z', active: sortMode.value === 'name-asc' },
      { key: 'name-desc', label: 'Name Z-A', active: sortMode.value === 'name-desc' },
    ]
  }

  return [
    { key: 'position', label: 'Recommended', first: true, active: sortMode.value === 'position' },
    { key: 'title-asc', label: 'Title A-Z', active: sortMode.value === 'title-asc' },
    { key: 'title-desc', label: 'Title Z-A', active: sortMode.value === 'title-desc' },
    { key: 'oldest', label: 'Oldest', active: sortMode.value === 'oldest' },
    { key: 'newest', label: 'Newest', active: sortMode.value === 'newest' },
  ]
})
const discoverSortRecommendedBreakVisible = computed(() => currentTab.value !== 'people')

const viewModeButtons = computed(() => ([
  { key: '1', label: 'Detailed view', first: true, active: currentViewMode.value === '1' },
  { key: '2', label: 'Poster grid view', active: currentViewMode.value === '2' },
  { key: '3', label: 'Compact list view', active: currentViewMode.value === '3' },
  { key: '4', label: 'Text list view', active: currentViewMode.value === '4' },
]))

const visibleViewModeButtons = computed(() => {
  if (currentTab.value === 'people') {
    return viewModeButtons.value.filter((button) => button.key !== '4')
  }
  return viewModeButtons.value
})

function countMatches(items, predicate) {
  return items.reduce((total, item) => (predicate(item) ? total + 1 : total), 0)
}

function filmOverviewEntryKey(entry) {
  if (entry?.film?.id) {
    return entry.film.id
  }
  if (entry?.id) {
    return entry.id
  }
  if (entry?.film?.slug) {
    return entry.film.slug
  }
  return 'unknown'
}

function isFilmOverviewExpanded(entry) {
  const key = filmOverviewEntryKey(entry)
  return Boolean(key && expandedFilmOverviewIds.value[key])
}

function hasExpandableFilmOverview(entry) {
  return hasExpandableOverview(entry?.film?.overview || '', 240)
}

function displayedFilmOverview(entry) {
  const overview = String(entry?.film?.overview || '').trim()
  if (!hasExpandableFilmOverview(entry) || isFilmOverviewExpanded(entry)) {
    return overview
  }
  return clipOverviewWithSuffix(overview, 240, '')
}

function shouldAddFilmDetailsTopGap(entry, rowType) {
  if (rowType === 'directors') {
    return Boolean(entry?.film?.overview)
  }
  if (rowType === 'stars') {
    return Boolean(entry?.film?.overview) && !entry?.film?.directors?.length
  }
  return false
}

function showWatchlistButtonText(entry) {
  return entry?.show?.watchlisted ? 'Watchlisted' : 'Add to watchlist'
}

function showWatchlistButtonShortText(entry) {
  return entry?.show?.watchlisted ? 'Watchlisted' : 'Watchlist'
}

function showFavouriteButtonText(entry) {
  return entry?.show?.favourited ? 'Favourited' : 'Add to favourites'
}

function showFavouriteButtonShortText(entry) {
  return entry?.show?.favourited ? 'Favourited' : 'Favourite'
}

function toggleFilmOverview(entry) {
  const key = filmOverviewEntryKey(entry)
  if (!key) {
    return
  }

  expandedFilmOverviewIds.value = {
    ...expandedFilmOverviewIds.value,
    [key]: !expandedFilmOverviewIds.value[key],
  }
}

const filmReleaseCounts = computed(() => ({
  all: Number(filmFilterCounts.value.release_status?.all ?? filmResults.value.length),
  released: Number(filmFilterCounts.value.release_status?.released ?? countMatches(filmResults.value, (item) => item.film.release_status === 'released')),
  unreleased: Number(filmFilterCounts.value.release_status?.unreleased ?? countMatches(filmResults.value, (item) => item.film.release_status === 'unreleased')),
}))

const filmReleaseScopedItems = computed(() => {
  if (currentTab.value !== 'films' || selectedReleaseStatus.value === 'all') {
    return filmResults.value
  }
  return filmResults.value.filter((item) => item.film.release_status === selectedReleaseStatus.value)
})

const filmActivityCounts = computed(() => ({
  all: Number(filmFilterCounts.value.activity?.all ?? filmReleaseScopedItems.value.length),
  watched: Number(filmFilterCounts.value.activity?.watched ?? countMatches(filmReleaseScopedItems.value, (item) => Boolean(item.film.watched))),
  unwatched: Number(filmFilterCounts.value.activity?.unwatched ?? countMatches(filmReleaseScopedItems.value, (item) => !item.film.watched)),
  watchlisted: Number(filmFilterCounts.value.activity?.watchlisted ?? countMatches(filmReleaseScopedItems.value, (item) => Boolean(item.film.watchlisted))),
  rated: Number(filmFilterCounts.value.activity?.rated ?? countMatches(filmReleaseScopedItems.value, (item) => normalizeRatingValue(item.film.rating) !== null)),
  favourited: Number(filmFilterCounts.value.activity?.favourited ?? countMatches(filmReleaseScopedItems.value, (item) => Boolean(item.film.favourited))),
}))

const showStatusCounts = computed(() => ({
  all: Number(showFilterCounts.value.release_status?.all ?? showResults.value.length),
  upcoming: Number(showFilterCounts.value.release_status?.upcoming ?? countMatches(showResults.value, (item) => item.show.release_status === 'upcoming')),
  ongoing: Number(showFilterCounts.value.release_status?.ongoing ?? countMatches(showResults.value, (item) => item.show.release_status === 'ongoing')),
  ended: Number(showFilterCounts.value.release_status?.ended ?? countMatches(showResults.value, (item) => item.show.release_status === 'ended')),
}))

const showStatusScopedItems = computed(() => {
  if (currentTab.value !== 'shows' || selectedReleaseStatus.value === 'all') {
    return showResults.value
  }
  return showResults.value.filter((item) => item.show.release_status === selectedReleaseStatus.value)
})

const showActivityCounts = computed(() => ({
  all: Number(showFilterCounts.value.activity?.all ?? showStatusScopedItems.value.length),
  watchlisted: Number(showFilterCounts.value.activity?.watchlisted ?? countMatches(showStatusScopedItems.value, (item) => Boolean(item.show.watchlisted))),
  rated: Number(showFilterCounts.value.activity?.rated ?? countMatches(showStatusScopedItems.value, (item) => normalizeRatingValue(item.show.rating) !== null)),
  favourited: Number(showFilterCounts.value.activity?.favourited ?? countMatches(showStatusScopedItems.value, (item) => Boolean(item.show.favourited))),
}))

const peopleActivityCounts = computed(() => ({
  all: Number(personFilterCounts.value.activity?.all ?? personResults.value.length),
  favourited: Number(personFilterCounts.value.activity?.favourited ?? countMatches(personResults.value, (item) => Boolean(item.person.favourited))),
}))

function buildFilterOptions(allCount, selectedValue, definitions) {
  const specific = definitions
    .filter((definition) => definition.count > 0)
    .map((definition) => ({
      value: definition.value,
      label: `${definition.label} (${definition.count})`,
      active: selectedValue === definition.value,
    }))

  if (specific.length === 0) {
    return [{ value: 'all', label: `All (${allCount})`, active: selectedValue === 'all' }]
  }

  return [
    { value: 'all', label: `All (${allCount})`, active: selectedValue === 'all' },
    ...specific,
  ]
}

const filterGroups = computed(() => {
  if (currentTab.value === 'films') {
    const groups = [
      {
        key: 'release-status',
        title: 'Release status:',
        options: buildFilterOptions(filmReleaseCounts.value.all, selectedReleaseStatus.value, [
          { value: 'released', label: 'Released', count: filmReleaseCounts.value.released },
          { value: 'unreleased', label: 'Upcoming', count: filmReleaseCounts.value.unreleased },
        ]),
      },
    ]

    if (isAuthenticated.value) {
      groups.push({
        key: 'activity',
        title: 'Your activity',
        options: buildFilterOptions(filmActivityCounts.value.all, selectedActivity.value, [
          { value: 'watched', label: 'Watched', count: filmActivityCounts.value.watched },
          { value: 'unwatched', label: 'Unwatched', count: filmActivityCounts.value.unwatched },
          { value: 'watchlisted', label: 'Watchlisted', count: filmActivityCounts.value.watchlisted },
          { value: 'rated', label: 'Rated', count: filmActivityCounts.value.rated },
          { value: 'favourited', label: 'Favourited', count: filmActivityCounts.value.favourited },
        ]),
      })
    }
    return groups
  }

  if (currentTab.value === 'shows') {
    const groups = [
      {
        key: 'release-status',
        title: 'Status:',
        options: buildFilterOptions(showStatusCounts.value.all, selectedReleaseStatus.value, [
          { value: 'upcoming', label: 'Upcoming', count: showStatusCounts.value.upcoming },
          { value: 'ongoing', label: 'Ongoing', count: showStatusCounts.value.ongoing },
          { value: 'ended', label: 'Ended', count: showStatusCounts.value.ended },
        ]),
      },
    ]

    if (isAuthenticated.value) {
      groups.push({
        key: 'activity',
        title: 'Your activity',
        options: buildFilterOptions(showActivityCounts.value.all, selectedActivity.value, [
          { value: 'watchlisted', label: 'Watchlisted', count: showActivityCounts.value.watchlisted },
          { value: 'rated', label: 'Rated', count: showActivityCounts.value.rated },
          { value: 'favourited', label: 'Favourited', count: showActivityCounts.value.favourited },
        ]),
      })
    }
    return groups
  }

  if (!isAuthenticated.value) {
    return []
  }

  return [
    {
      key: 'activity',
      title: 'Your activity',
      options: buildFilterOptions(peopleActivityCounts.value.all, selectedActivity.value, [
        { value: 'favourited', label: 'Favourited', count: peopleActivityCounts.value.favourited },
      ]),
    },
  ]
})

const hasFilterGroups = computed(() => filterGroups.value.length > 0)
const discoverFilterModalRows = computed(() => {
  const groups = filterGroups.value
  return [
    ...Array.from({ length: Math.ceil(groups.length / 2) }, (_, index) => {
      const rowGroups = groups.slice(index * 2, index * 2 + 2)
      return {
        key: rowGroups.map((group) => group.key).join('-'),
        groups: rowGroups,
        full: rowGroups.length === 1,
      }
    }),
  ].filter((row) => row.groups.length)
})
const filtersColumnVisible = computed(() => (
  hasFilterGroups.value && viewportWidth.value > discoverFiltersColumnMaxWidth
))
const filtersButtonVisible = computed(() => (
  hasFilterGroups.value && !filtersColumnVisible.value
))
const filterButtonLabel = computed(() => {
  return 'Filters'
})

const filteredItems = computed(() => {
  if (currentTab.value === 'films') {
    let items = [...filmReleaseScopedItems.value]
    if (isAuthenticated.value && selectedActivity.value !== 'all') {
      items = items.filter((item) => {
        switch (selectedActivity.value) {
          case 'watched':
            return Boolean(item.film.watched)
          case 'unwatched':
            return !item.film.watched
          case 'watchlisted':
            return Boolean(item.film.watchlisted)
          case 'rated':
            return normalizeRatingValue(item.film.rating) !== null
          case 'favourited':
            return Boolean(item.film.favourited)
          default:
            return true
        }
      })
    }
    return items
  }

  if (currentTab.value === 'shows') {
    let items = [...showStatusScopedItems.value]
    if (isAuthenticated.value && selectedActivity.value !== 'all') {
      items = items.filter((item) => {
        switch (selectedActivity.value) {
          case 'watchlisted':
            return Boolean(item.show.watchlisted)
          case 'rated':
            return normalizeRatingValue(item.show.rating) !== null
          case 'favourited':
            return Boolean(item.show.favourited)
          default:
            return true
        }
      })
    }
    return items
  }

  if (isAuthenticated.value && selectedActivity.value === 'favourited') {
    return personResults.value.filter((item) => Boolean(item.person.favourited))
  }
  return personResults.value
})

function numericYear(value) {
  const text = String(value || '')
  const match = text.match(/\d{4}/)
  return match ? Number.parseInt(match[0], 10) : 0
}

function titleSortKey(value) {
  return String(value || '').replace(/^the\s+/i, '')
}

const sortedItems = computed(() => {
  const items = [...filteredItems.value]
  const sortString = (left, right, extractor) => (
    String(extractor(left) || '').localeCompare(String(extractor(right) || ''), undefined, { sensitivity: 'base' })
  )
  const sortTitle = (left, right) => (
    sortString(left, right, (item) => titleSortKey(item.film?.title || item.show?.title))
    || sortString(left, right, (item) => item.film?.title || item.show?.title)
  )

  switch (sortMode.value) {
    case 'title-asc':
      return items.sort((left, right) => sortTitle(left, right))
    case 'title-desc':
      return items.sort((left, right) => sortTitle(right, left))
    case 'name-asc':
      return items.sort((left, right) => sortString(left, right, (item) => item.person?.name))
    case 'name-desc':
      return items.sort((left, right) => sortString(right, left, (item) => item.person?.name))
    case 'oldest':
      return items.sort((left, right) => numericYear(left.film?.year || left.show?.year) - numericYear(right.film?.year || right.show?.year))
    case 'newest':
      return items.sort((left, right) => numericYear(right.film?.year || right.show?.year) - numericYear(left.film?.year || left.show?.year))
    case 'position':
    default:
      return items.sort((left, right) => (left.position - right.position) || (right.score - left.score))
  }
})

const effectiveNumPages = computed(() => {
  if (currentTab.value === 'films') {
    return Math.max(1, filmNumPages.value)
  }
  if (currentTab.value === 'shows') {
    return Math.max(1, showNumPages.value)
  }
  if (currentTab.value === 'people') {
    return Math.max(1, personNumPages.value)
  }
  return Math.max(1, Math.ceil(sortedItems.value.length / pageSize))
})
const pages = computed(() => compactPaginationItems(currentPage.value, effectiveNumPages.value))
const paginatedItems = computed(() => {
  if (currentTab.value === 'films' || currentTab.value === 'shows' || currentTab.value === 'people') {
    return sortedItems.value
  }
  const start = (currentPage.value - 1) * pageSize
  return sortedItems.value.slice(start, start + pageSize)
})

const selectedRatingValue = computed(() => {
  if (!selectedRatingItem.value) {
    return null
  }
  return normalizeRatingValue(
    selectedRatingKind.value === 'show'
      ? selectedRatingItem.value.show?.rating
      : selectedRatingItem.value.film?.rating
  )
})

const ratingPickerTitle = computed(() => {
  if (!selectedRatingItem.value) return 'item'
  return selectedRatingKind.value === 'show'
    ? selectedRatingItem.value.show?.title || 'show'
    : selectedRatingItem.value.film?.title || 'film'
})

function singleQueryValue(value) {
  return Array.isArray(value) ? value[0] : value
}

function normalizedPage(value) {
  const parsed = Number.parseInt(String(singleQueryValue(value) || '1'), 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
}

function normalizedTab(value) {
  const tab = String(value || '').toLowerCase()
  return ['films', 'shows', 'people'].includes(tab) ? tab : 'films'
}

function discoverPathForTab(tab) {
  return `/discover/${normalizedTab(tab)}`
}

function normalizedFilmSection(value) {
  return String(value || '').toLowerCase() === 'all-time-greats'
    ? 'all-time-greats'
    : 'popular'
}

function discoverFilmSectionPath(section) {
  return `${discoverPathForTab('films')}/${normalizedFilmSection(section)}`
}

function allowedSortModes(tab) {
  if (tab === 'people') {
    return ['position', 'name-asc', 'name-desc']
  }
  return ['position', 'title-asc', 'title-desc', 'oldest', 'newest']
}

function normalizedSortMode(tab, value) {
  const mode = String(singleQueryValue(value) || 'position')
  return allowedSortModes(tab).includes(mode) ? mode : 'position'
}

function allowedReleaseStatuses(tab) {
  if (tab === 'films') return ['all', 'released', 'unreleased']
  if (tab === 'shows') return ['all', 'upcoming', 'ongoing', 'ended']
  return ['all']
}

function normalizedReleaseStatus(tab, value) {
  const status = String(singleQueryValue(value) || 'all')
  return allowedReleaseStatuses(tab).includes(status) ? status : 'all'
}

function allowedActivities(tab) {
  if (tab === 'films') return ['all', 'watched', 'unwatched', 'watchlisted', 'rated', 'favourited']
  if (tab === 'shows') return ['all', 'watchlisted', 'rated', 'favourited']
  return ['all', 'favourited']
}

function normalizedActivity(tab, value) {
  const activity = String(singleQueryValue(value) || 'all')
  return allowedActivities(tab).includes(activity) ? activity : 'all'
}

function normalizedViewMode(tab, value) {
  const mode = String(singleQueryValue(value) || localStorage.getItem(discoverViewModeStorageKey) || '1')
  if (tab === 'people') {
    return ['1', '2', '3'].includes(mode) ? mode : '1'
  }
  return ['1', '2', '3', '4'].includes(mode) ? mode : '1'
}

function routeStateFromRoute(targetRoute = route) {
  const tab = normalizedTab(targetRoute.params?.tab)
  return {
    tab,
    page: normalizedPage(targetRoute.query?.page),
    releaseStatus: normalizedReleaseStatus(tab, targetRoute.query?.release_status),
    activity: normalizedActivity(tab, targetRoute.query?.activity),
    view: normalizedViewMode(tab, targetRoute.query?.view),
    sort: normalizedSortMode(tab, targetRoute.query?.sort),
  }
}

function discoverQuery(overrides = {}) {
  return {
    page: String(overrides.page ?? currentPage.value),
    release_status: String(overrides.releaseStatus ?? selectedReleaseStatus.value),
    activity: String(overrides.activity ?? selectedActivity.value),
    view: String(overrides.view ?? currentViewMode.value),
    sort: String(overrides.sort ?? sortMode.value),
  }
}

function currentDiscoverLocation(overrides = {}) {
  const tab = normalizedTab(overrides.tab ?? currentTab.value)
  const path = tab === 'films'
    ? discoverFilmSectionPath(route.params.section)
    : discoverPathForTab(tab)
  return {
    path,
    query: discoverQuery({
      page: normalizedPage(overrides.page ?? currentPage.value),
      releaseStatus: normalizedReleaseStatus(tab, overrides.releaseStatus ?? selectedReleaseStatus.value),
      activity: normalizedActivity(tab, overrides.activity ?? selectedActivity.value),
      view: normalizedViewMode(tab, overrides.view ?? currentViewMode.value),
      sort: normalizedSortMode(tab, overrides.sort ?? sortMode.value),
    }),
  }
}

function syncStateFromRoute(targetRoute = route) {
  const nextState = routeStateFromRoute(targetRoute)
  syncingFromRoute.value = true
  currentPage.value = nextState.page
  selectedReleaseStatus.value = nextState.releaseStatus
  selectedActivity.value = nextState.activity
  currentViewMode.value = nextState.view
  sortMode.value = nextState.sort
  nextTick(() => {
    syncingFromRoute.value = false
  })
  return nextState
}

async function pushDiscoverState(overrides = {}, { scroll = false } = {}) {
  const location = currentDiscoverLocation(overrides)
  await router.push(location)
  if (scroll) {
    await scrollListStartIntoView()
  }
}

function syncViewportWidth() {
  if (typeof window !== 'undefined') {
    viewportWidth.value = window.innerWidth
  }
}

onMounted(() => {
  syncViewportWidth()
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', syncViewportWidth)
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', syncViewportWidth)
  }
})

watch(currentViewMode, (value) => {
  localStorage.setItem(discoverViewModeStorageKey, value)
})

watch(effectiveNumPages, (value) => {
  if (currentPage.value > value) {
    pushDiscoverState({ page: value }, { scroll: false })
  }
})

watch(filtersColumnVisible, (visible) => {
  if (visible) {
    filtersModalOpen.value = false
  }
})

watch(() => route.fullPath, async () => {
  syncStateFromRoute(route)
  setDocumentTitle(discoverDocumentTitle.value)
  closeRatingPicker()
  await loadCurrentTab()
}, { immediate: true })

function openMobileSearchOverlay() {
  mobileSearchOpen.value = true
}

function closeMobileSearchOverlay() {
  mobileSearchOpen.value = false
}

function buildAuthHeaders() {
  const headers = {}
  if (auth.state.access) {
    headers.Authorization = `Bearer ${auth.state.access}`
  }
  return headers
}

function buildJsonAuthHeaders() {
  return {
    ...buildAuthHeaders(),
    'Content-Type': 'application/json',
  }
}

async function loadCurrentTab() {
  if (currentTab.value === 'shows') {
    await loadShows()
    return
  }
  if (currentTab.value === 'people') {
    await loadPeople()
    return
  }
  await loadFilms()
}

async function loadFilms() {
  filmLoading.value = true
  filmError.value = ''
  try {
    const category = currentFilmSubtab.value === 'all-time-greats' ? 'all-time' : 'popular'
    const params = new URLSearchParams({
      category,
      page: String(currentPage.value),
      release_status: selectedReleaseStatus.value,
      activity: selectedActivity.value,
      sort: sortMode.value,
    })
    const url = `/api/v1/discover/films/?${params.toString()}`
    const response = isAuthenticated.value
      ? await fetch(url, { headers: buildAuthHeaders() })
      : await fetch(url)
    if (!response.ok) {
      filmError.value = 'Unable to load discover films right now.'
      filmResults.value = []
      filmCount.value = 0
      filmNumPages.value = 1
      filmFilterCounts.value = {}
      return
    }
    const data = await response.json()
    filmResults.value = Array.isArray(data.results) ? data.results : []
    filmCount.value = Number.isFinite(Number(data.count)) ? Number(data.count) : filmResults.value.length
    filmNumPages.value = Number.isFinite(Number(data.num_pages))
      ? Math.max(1, Number(data.num_pages))
      : Math.max(1, Math.ceil(filmCount.value / filmPageSize))
    filmFilterCounts.value = data.filter_counts || {}
  } catch {
    filmError.value = 'Unable to load discover films right now.'
    filmResults.value = []
    filmCount.value = 0
    filmNumPages.value = 1
    filmFilterCounts.value = {}
  } finally {
    filmLoading.value = false
  }
}

async function loadShows() {
  showLoading.value = true
  showError.value = ''
  try {
    const params = new URLSearchParams({
      page: String(currentPage.value),
      release_status: selectedReleaseStatus.value,
      activity: selectedActivity.value,
      sort: sortMode.value,
    })
    const url = `/api/v1/discover/shows/?${params.toString()}`
    const response = isAuthenticated.value
      ? await fetch(url, { headers: buildAuthHeaders() })
      : await fetch(url)
    if (!response.ok) {
      showError.value = 'Unable to load discover shows right now.'
      showResults.value = []
      showCount.value = 0
      showNumPages.value = 1
      showFilterCounts.value = {}
      return
    }
    const data = await response.json()
    showResults.value = Array.isArray(data.results) ? data.results : []
    showCount.value = Number.isFinite(Number(data.count)) ? Number(data.count) : showResults.value.length
    showNumPages.value = Number.isFinite(Number(data.num_pages))
      ? Math.max(1, Number(data.num_pages))
      : Math.max(1, Math.ceil(showCount.value / showPageSize))
    showFilterCounts.value = data.filter_counts || {}
  } catch {
    showError.value = 'Unable to load discover shows right now.'
    showResults.value = []
    showCount.value = 0
    showNumPages.value = 1
    showFilterCounts.value = {}
  } finally {
    showLoading.value = false
  }
}

async function loadPeople() {
  personLoading.value = true
  personError.value = ''
  try {
    const params = new URLSearchParams({
      page: String(currentPage.value),
      activity: selectedActivity.value,
      sort: sortMode.value,
    })
    const url = `/api/v1/discover/people/?${params.toString()}`
    const response = isAuthenticated.value
      ? await fetch(url, { headers: buildAuthHeaders() })
      : await fetch(url)
    if (!response.ok) {
      personError.value = 'Unable to load discover people right now.'
      personResults.value = []
      personCount.value = 0
      personNumPages.value = 1
      personFilterCounts.value = {}
      return
    }
    const data = await response.json()
    personResults.value = Array.isArray(data.results) ? data.results : []
    personCount.value = Number.isFinite(Number(data.count)) ? Number(data.count) : personResults.value.length
    personNumPages.value = Number.isFinite(Number(data.num_pages))
      ? Math.max(1, Number(data.num_pages))
      : Math.max(1, Math.ceil(personCount.value / personPageSize))
    personFilterCounts.value = data.filter_counts || {}
  } catch {
    personError.value = 'Unable to load discover people right now.'
    personResults.value = []
    personCount.value = 0
    personNumPages.value = 1
    personFilterCounts.value = {}
  } finally {
    personLoading.value = false
  }
}

function setSortMode(mode) {
  if (syncingFromRoute.value) return
  pushDiscoverState({ page: 1, sort: mode })
}

function setViewMode(mode) {
  if (syncingFromRoute.value) return
  pushDiscoverState({ view: mode })
}

function applyFilter(groupKey, value) {
  if (syncingFromRoute.value) return
  if (groupKey === 'release-status') {
    pushDiscoverState({ page: 1, releaseStatus: value })
    return
  }
  if (groupKey === 'activity') {
    pushDiscoverState({ page: 1, activity: value })
  }
}

function toggleFilters() {
  filtersModalOpen.value = !filtersModalOpen.value
}

async function scrollListStartIntoView() {
  await nextTick()
  listContentStartRef.value?.scrollIntoView?.({ block: 'start', behavior: 'auto' })
}

async function goToPage(pageNumber) {
  if (pageNumber < 1 || pageNumber > effectiveNumPages.value || pageNumber === currentPage.value) return
  await pushDiscoverState({ page: pageNumber }, { scroll: true })
}

function normalizeRatingValue(value) {
  const parsed = Number.parseInt(String(value ?? ''), 10)
  if (Number.isNaN(parsed) || parsed < 1 || parsed > 10) {
    return null
  }
  return parsed
}

function ratingText(value) {
  const normalized = normalizeRatingValue(value)
  return normalized ? `${normalized} / 10` : '/ 10'
}

function compactRatingEntryKey(kind, entry) {
  if (kind === 'show') {
    return entry?.show?.id ? `show:${entry.show.id}` : ''
  }
  return entry?.film?.id ? `film:${entry.film.id}` : ''
}

function compactRatingEntryValue(kind, entry) {
  return normalizeRatingValue(kind === 'show' ? entry?.show?.rating : entry?.film?.rating)
}

function previewCompactRating(kind, entry, star) {
  const key = compactRatingEntryKey(kind, entry)
  if (!key) return
  compactRatingPreviewByKey.value = {
    ...compactRatingPreviewByKey.value,
    [key]: star,
  }
}

function resetCompactRatingPreview(kind, entry) {
  const key = compactRatingEntryKey(kind, entry)
  if (!key || !(key in compactRatingPreviewByKey.value)) return

  const next = { ...compactRatingPreviewByKey.value }
  delete next[key]
  compactRatingPreviewByKey.value = next
}

function compactRatingStarClass(kind, entry, star) {
  const key = compactRatingEntryKey(kind, entry)
  const currentRating = (key ? compactRatingPreviewByKey.value[key] : 0) || compactRatingEntryValue(kind, entry) || 0
  return [
    'rating-star',
    star <= currentRating ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

function summaryRatingStarClass(value) {
  return [
    'rating-star',
    normalizeRatingValue(value) ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

function openRatingPicker(kind, entry) {
  selectedRatingKind.value = kind
  selectedRatingItem.value = entry
  ratingPreview.value = 0
}

function closeRatingPicker() {
  selectedRatingKind.value = ''
  selectedRatingItem.value = null
  ratingPreview.value = 0
}

function previewRating(star) {
  ratingPreview.value = star
}

function resetRatingPreview() {
  ratingPreview.value = 0
}

function ratingPickerStarClass(star) {
  const currentRating = ratingPreview.value || selectedRatingValue.value || 0
  return [
    'rating-star',
    star <= currentRating ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

async function selectRating(ratingValue) {
  if (!selectedRatingItem.value) return

  if (selectedRatingKind.value === 'show') {
    const response = await fetch(`/api/v1/shows/${selectedRatingItem.value.show.slug}/rating`, {
      method: 'POST',
      headers: buildJsonAuthHeaders(),
      body: JSON.stringify({ rating: ratingValue, source: showInteractionSource }),
    })
    if (response.ok) {
      const data = await response.json()
      selectedRatingItem.value.show.rating = normalizeRatingValue(data.rating)
    }
  } else {
    const response = await fetch(`/api/v1/films/${selectedRatingItem.value.film.slug}/rating`, {
      method: 'POST',
      headers: buildJsonAuthHeaders(),
      body: JSON.stringify({ rating: ratingValue, source: filmInteractionSource }),
    })
    if (response.ok) {
      const data = await response.json()
      selectedRatingItem.value.film.rating = normalizeRatingValue(data.rating)
      if (!selectedRatingItem.value.film.watched) {
        await markFilmWatched(selectedRatingItem.value)
      }
    }
  }

  closeRatingPicker()
}

async function setCompactRating(kind, entry, ratingValue) {
  if (kind === 'show') {
    const response = await fetch(`/api/v1/shows/${entry.show.slug}/rating`, {
      method: 'POST',
      headers: buildJsonAuthHeaders(),
      body: JSON.stringify({ rating: ratingValue, source: showInteractionSource }),
    })
    if (response.ok) {
      const data = await response.json()
      entry.show.rating = normalizeRatingValue(data.rating)
    }
    resetCompactRatingPreview('show', entry)
    return
  }

  const response = await fetch(`/api/v1/films/${entry.film.slug}/rating`, {
    method: 'POST',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({ rating: ratingValue, source: filmInteractionSource }),
  })
  if (response.ok) {
    const data = await response.json()
    entry.film.rating = normalizeRatingValue(data.rating)
    if (!entry.film.watched) {
      await markFilmWatched(entry)
    }
  }
  resetCompactRatingPreview('film', entry)
}

async function resetSelectedItemRating() {
  if (!selectedRatingItem.value) return

  if (selectedRatingKind.value === 'show') {
    const response = await fetch(`/api/v1/shows/${selectedRatingItem.value.show.slug}/rating`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })
    if (response.ok) {
      selectedRatingItem.value.show.rating = null
    }
  } else {
    const response = await fetch(`/api/v1/films/${selectedRatingItem.value.film.slug}/rating`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })
    if (response.ok) {
      selectedRatingItem.value.film.rating = null
    }
  }

  closeRatingPicker()
}

async function markFilmWatched(entry) {
  const response = await fetch(`/api/v1/films/${entry.film.slug}/watch`, {
    method: 'POST',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({ source: filmInteractionSource }),
  })
  if (response.ok) {
    entry.film.watched = true
  }
}

async function toggleFilmWatchlist(entry) {
  const response = await fetch(`/api/v1/films/${entry.film.slug}/watchlist`, {
    method: 'POST',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({ source: filmInteractionSource }),
  })
  if (response.ok) {
    const data = await response.json()
    entry.film.watchlisted = Boolean(data.watchlisted)
  }
}

async function toggleFilmFavourite(entry) {
  const response = await fetch(`/api/v1/films/${entry.film.slug}/favourite`, {
    method: 'POST',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({ source: filmInteractionSource }),
  })
  if (response.ok) {
    const data = await response.json()
    entry.film.favourited = Boolean(data.favourited)
  }
}

async function toggleShowWatchlist(entry) {
  const response = await fetch(`/api/v1/shows/${entry.show.slug}/watchlist`, {
    method: 'POST',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({ source: showInteractionSource }),
  })
  if (response.ok) {
    const data = await response.json()
    entry.show.watchlisted = Boolean(data.watchlisted)
  }
}

async function toggleShowFavourite(entry) {
  const response = await fetch(`/api/v1/shows/${entry.show.slug}/favourite`, {
    method: 'POST',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({ source: showInteractionSource }),
  })
  if (response.ok) {
    const data = await response.json()
    entry.show.favourited = Boolean(data.favourited)
  }
}

async function togglePersonFavourite(entry) {
  const response = await fetch(`/api/v1/people/${entry.person.person_id}/favourite`, {
    method: 'POST',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({ source: personInteractionSource }),
  })
  if (response.ok) {
    const data = await response.json()
    entry.person.favourited = Boolean(data.favourited)
  }
}
</script>
